import { AuthorityName } from '@fides/fides-react-components';
import { ClientFileStatus, SourceType } from 'core/enums';

type HasAuthorityName = (authorityName: AuthorityName) => boolean;

const isOpenUploadedPaymentNotDeleted = (
  sourceType?: SourceType | null,
  clientFileStatus?: ClientFileStatus | null,
  deleted?: boolean
): boolean =>
  !deleted &&
  clientFileStatus === ClientFileStatus.OPEN &&
  (sourceType === SourceType.UPLOAD || sourceType === SourceType.FILE_ENTRY);

const isOpenManualPaymentNotDeleted = (
  sourceType?: SourceType | null,
  clientFileStatus?: ClientFileStatus | null,
  deleted?: boolean
): boolean =>
  !deleted &&
  clientFileStatus === ClientFileStatus.OPEN &&
  (sourceType === SourceType.EFT_WEB || sourceType === SourceType.CPS_WEB);

export interface PaymentPermissions {
  canCreatePaymentsOrFromTemplate: boolean;
  canFullyEditUploadedPayments: boolean;
  canEditUploadedPaymentByPermissions: boolean;
  canEditManualPaymentByPermissions: boolean;
  canAccessAnyPaymentEditByPermissions: boolean;
  canAccessPaymentEditByTypeAndPermissions: (params: {
    sourceType?: SourceType | null;
    clientFileStatus?: ClientFileStatus | null;
    deleted?: boolean;
  }) => boolean;
  canAccessPaymentEditRouteByPayment: (params: {
    impersonated?: boolean;
    sourceType?: SourceType | null;
    clientFileStatus?: ClientFileStatus | null;
    deleted?: boolean;
  }) => boolean;
  canFullyEditPaymentByTypeAndPermissions: (params: {
    sourceType?: SourceType | null;
    clientFileStatus?: ClientFileStatus | null;
    deleted?: boolean;
  }) => boolean;
  canAccessFullPaymentEditRouteByPayment: (params: {
    impersonated?: boolean;
    sourceType?: SourceType | null;
    clientFileStatus?: ClientFileStatus | null;
    deleted?: boolean;
  }) => boolean;
}

export const computePaymentPermissions = (hasAuthorityName: HasAuthorityName): PaymentPermissions => {
  const hasCreateAny = hasAuthorityName('PAYMENT_CREATE_ANY_GRANT');
  const hasCreateFromTemplate = hasAuthorityName('PAYMENT_CREATE_FROM_TEMPLATE_GRANT');
  const hasEditFullUploaded = hasAuthorityName('PAYMENT_EDIT_FULL_UPLOADED_GRANT');
  const hasDeleteTransactionsUploaded = hasAuthorityName('PAYMENT_DELETE_TRANSACTIONS_UPLOADED_GRANT');
  const hasEditFull = hasAuthorityName('PAYMENT_EDIT_FULL_GRANT');

  const canCreatePaymentsOrFromTemplate = hasCreateAny || hasCreateFromTemplate;
  const canFullyEditUploadedPayments = canCreatePaymentsOrFromTemplate && hasEditFullUploaded;
  const canEditUploadedPaymentByPermissions = canFullyEditUploadedPayments || hasDeleteTransactionsUploaded;
  const canEditManualPaymentByPermissions = canCreatePaymentsOrFromTemplate && hasEditFull;
  const canAccessAnyPaymentEditByPermissions = canEditUploadedPaymentByPermissions || canEditManualPaymentByPermissions;

  const canAccessPaymentEditByTypeAndPermissions = ({
    sourceType,
    clientFileStatus,
    deleted,
  }: {
    sourceType?: SourceType | null;
    clientFileStatus?: ClientFileStatus | null;
    deleted?: boolean;
  }): boolean =>
    (isOpenUploadedPaymentNotDeleted(sourceType, clientFileStatus, deleted) && canEditUploadedPaymentByPermissions) ||
    (isOpenManualPaymentNotDeleted(sourceType, clientFileStatus, deleted) && canEditManualPaymentByPermissions);

  const canAccessPaymentEditRouteByPayment = ({
    impersonated,
    sourceType,
    clientFileStatus,
    deleted,
  }: {
    impersonated?: boolean;
    sourceType?: SourceType | null;
    clientFileStatus?: ClientFileStatus | null;
    deleted?: boolean;
  }): boolean => !impersonated && canAccessPaymentEditByTypeAndPermissions({ sourceType, clientFileStatus, deleted });

  // uploaded or manual payments. Used where "edit access" must mean full editing, not delete-only.
  const canFullyEditPaymentByTypeAndPermissions = ({
    sourceType,
    clientFileStatus,
    deleted,
  }: {
    sourceType?: SourceType | null;
    clientFileStatus?: ClientFileStatus | null;
    deleted?: boolean;
  }): boolean =>
    (isOpenUploadedPaymentNotDeleted(sourceType, clientFileStatus, deleted) && canFullyEditUploadedPayments) ||
    (isOpenManualPaymentNotDeleted(sourceType, clientFileStatus, deleted) && canEditManualPaymentByPermissions);

  const canAccessFullPaymentEditRouteByPayment = ({
    impersonated,
    sourceType,
    clientFileStatus,
    deleted,
  }: {
    impersonated?: boolean;
    sourceType?: SourceType | null;
    clientFileStatus?: ClientFileStatus | null;
    deleted?: boolean;
  }): boolean => !impersonated && canFullyEditPaymentByTypeAndPermissions({ sourceType, clientFileStatus, deleted });

  return {
    canCreatePaymentsOrFromTemplate,
    canFullyEditUploadedPayments,
    canEditUploadedPaymentByPermissions,
    canEditManualPaymentByPermissions,
    canAccessAnyPaymentEditByPermissions,
    canAccessPaymentEditByTypeAndPermissions,
    canAccessPaymentEditRouteByPayment,
    canFullyEditPaymentByTypeAndPermissions,
    canAccessFullPaymentEditRouteByPayment,
  };
};
